import { MutationOptions, useMutation, useQueryClient } from "react-query";
import { APIError, APIResponse, API_URL, post } from "../api";
import { PaymentData } from "../@types";

/**
 * Confirm-payment error codes the backend returns with HTTP 409. None of them are
 * safe to retry: the charge either already went through or is still being settled.
 */
export const BOOKING_PAYMENT_ERROR_CODES = [
  "WALLET_BOOKING_PENDING",
  "BOOKING_ALREADY_PAID",
  "BOOKING_ALREADY_TICKETED",
] as const;

export type BookingPaymentErrorCode =
  (typeof BOOKING_PAYMENT_ERROR_CODES)[number];

export type BookingPaymentErrorDetails = {
  error_code: BookingPaymentErrorCode;
  reference?: string;
  booking_id?: string;
  status?: string;
};

/** Any `error_code` the backend attached, whether or not it is one we act on. */
function readErrorCode(error: unknown): string | null {
  if (!(error instanceof APIError)) return null;

  const details = error.responseData?.data as
    | { error_code?: string }
    | undefined;

  return typeof details?.error_code === "string" ? details.error_code : null;
}

/** Pull the structured 409 payload off an error, or null if it isn't one of ours. */
export function getBookingPaymentErrorDetails(
  error: unknown
): BookingPaymentErrorDetails | null {
  if (!(error instanceof APIError)) return null;

  const details = error.responseData?.data as
    | Partial<BookingPaymentErrorDetails>
    | undefined;
  const code = details?.error_code;

  if (
    !code ||
    !BOOKING_PAYMENT_ERROR_CODES.includes(code as BookingPaymentErrorCode)
  ) {
    return null;
  }

  return {
    error_code: code as BookingPaymentErrorCode,
    reference: details?.reference,
    booking_id: details?.booking_id,
    status: details?.status,
  };
}

/**
 * Statuses on which the server definitely rejected the request before taking any
 * money: bad input, a missing booking, an expired session, insufficient funds, a
 * duplicate it refused. Anything outside this list — a 5xx, a status we never
 * saw, a body we could not read — leaves the outcome genuinely unknown.
 */
const DEFINITE_FAILURE_STATUSES = [400, 401, 403, 404, 409, 422];

/**
 * True when we cannot say whether the wallet was debited. Deliberately errs
 * towards "unknown": telling someone their payment failed when it succeeded
 * invites them to pay twice, while telling them we are checking costs them a
 * few minutes.
 */
export function isUnconfirmedOutcomeError(error: unknown): boolean {
  // Not one of our API errors, so nothing ever told us what the server did with
  // the request.
  if (!(error instanceof APIError)) return true;

  // No response at all, or a response whose body we could not decode.
  if (error.isNetworkError || error.isUnreadableResponse) return true;

  // The server refused to even start this attempt because another payment on the
  // account was still in flight — and that other one may be mid-charge.
  if (readErrorCode(error) === "PAYMENT_IN_PROGRESS") return true;

  // No status recorded means the error came from somewhere that never saw the
  // response either.
  if (typeof error.status !== "number") return true;

  // 5xx and anything else unrecognised: the server may have failed after the
  // debit landed.
  return !DEFINITE_FAILURE_STATUSES.includes(error.status);
}

function useCompleteBookingPayment(
  options?: MutationOptions<
    APIResponse<PaymentData>,
    APIError,
    Record<any, any>
  >
) {
  const client = useQueryClient();
  const {
    mutate: confirmBookingPayment,
    isLoading: confirmingBookingPayment,
    isError: isConfirmingPaymentError,
    error: confirmBookingPaymentError,
    data: paymentData,
  } = useMutation<APIResponse<PaymentData>, APIError, Record<any, any>>(
    async (variables) => {
      const token = localStorage.getItem("token");

      return await post(
        API_URL + "/aviation/flights/bookings/payments/confirm",
        variables,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    {
      ...options,
      onSettled(data, error, variables, context) {
        client.refetchQueries(["transactionHistory"]);
        options?.onSettled?.(data, error, variables, context);
      },
    }
  );

  return {
    paymentData,
    confirmBookingPayment,
    confirmingBookingPayment,
    isConfirmingPaymentError,
    confirmBookingPaymentError,
  };
}

export default useCompleteBookingPayment;
